import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime, formatDuration } from '../../lib/format.js';
import { DISPOSITION_GROUPS, DISPOSITION_GROUP_LABELS, type DispositionCode }
  from '@arcnet/contracts';

/**
 * What the call was (API dispositions.ts).
 *
 * Asked the moment a call ends, because the answer is only cheap then: a week later nobody
 * remembers, and a report of "other" is a report of nothing. The codes are the account's, and
 * some of them ask for a note -- a complaint without a word of explanation is a number on a
 * chart that cannot be acted on.
 *
 * In the workspace it takes the dial pad's place for the wrap-up time (owner, 3 October 2026), so
 * marking the call is the one thing to do then; when the time runs out the agent goes ready and
 * the call can still be marked from Recent calls.
 */

interface LastCall {
  id: string;
  call_uuid: string;
  direction: string;
  from_number: string | null;
  to_number: string | null;
  started_at: string;
  answered: boolean;
  duration_sec: number | null;
  disposition: string | null;
  note: string | null;
}

export function WrapUp({ tenantId, extensionId, endedAt, onMarked, embedded }: {
  tenantId: string;
  extensionId: string;
  /** When the workspace last saw a call end, so the panel appears for the call just finished. */
  endedAt: number;
  /** Marked: the agent may leave wrap-up now, where the account requires it (0110). */
  onMarked?: () => void;
  /** Drawn inside another card (the workspace's wrap-up stage), not as a card of its own. */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}`;
  const [note, setNote] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [needNote, setNeedNote] = useState(false);

  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: DispositionCode[] }>(`/v1/tenants/${tenantId}/disposition-codes`),
    staleTime: 10 * 60_000,
  });

  const call = useQuery({
    queryKey: ['last-call', tenantId, extensionId, endedAt],
    queryFn: () => api.get<LastCall | null>(`${base}/last-call`),
    enabled: endedAt > 0,
    // The CDR is written when the call ends and shipped to us a moment later; this is that moment.
    retry: 3,
    retryDelay: 2_000,
  });

  useEffect(() => {
    setChosen(call.data?.disposition ?? null);
    setNote(call.data?.note ?? '');
  }, [call.data?.call_uuid, call.data?.disposition, call.data?.note]);

  const save = useMutation({
    mutationFn: (v: { code: string; note: string | null }) =>
      api.put(`${base}/last-call/disposition`, {
        code: v.code, ...(v.note ? { note: v.note } : {}),
        ...(call.data?.call_uuid ? { call_uuid: call.data.call_uuid } : {}),
      }),
    onSuccess: (_r, v) => {
      setChosen(v.code);
      setNeedNote(false);
      void qc.invalidateQueries({ queryKey: ['last-call', tenantId, extensionId] });
      onMarked?.();
    },
  });

  if (endedAt === 0 || !call.data) return null;

  const active = (codes.data?.data ?? []).filter((c) => c.is_active);
  if (active.length === 0) return null;

  const c = call.data;
  const number = c.direction === 'outbound' ? c.to_number : c.from_number;

  const choose = (code: DispositionCode): void => {
    if (code.requires_note && !note.trim()) {
      setChosen(code.code);
      setNeedNote(true);
      return;
    }
    save.mutate({ code: code.code, note: note.trim() || null });
  };

  return (
    <section className={embedded ? 'arc-wrap' : 'arc-card arc-card-body arc-wrap'}>
      <div className="arc-row" style={{ justifyContent: 'space-between', gap: 10 }}>
        <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
          <strong style={{ fontSize: 13 }}>
            {chosen ? 'Call marked' : 'What was this call?'}
          </strong>
          <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>
            {number ?? 'withheld'} · {relativeTime(c.started_at)}
            {c.answered ? ` · ${formatDuration(c.duration_sec ?? 0)}` : ' · no answer'}
          </span>
        </div>
        {chosen && <Icon name="check" size={15} />}
      </div>

      {/* Grouped, because an agent is choosing between kinds of outcome before they choose a
          word for one: positive, neutral, negative, in that order every time, so the hand goes
          to the right part of the panel without reading it (owner, 24 September 2026). A group
          with nothing in it is not drawn -- an account that never marks anything negative
          should not have an empty heading on every call. */}
      {DISPOSITION_GROUPS.map((group) => {
        const codes = active.filter((c) => c.kind === group);
        if (codes.length === 0) return null;
        return (
          <div key={group} className="arc-wrap-group" data-group={group}>
            <span className="arc-wrap-group-label">{DISPOSITION_GROUP_LABELS[group]}</span>
            <div className="arc-wrap-codes">
              {codes.map((code) => (
                <button key={code.code} type="button"
                  className="arc-btn arc-btn-sm" data-kind={code.kind}
                  data-on={chosen === code.code ? '' : undefined}
                  disabled={save.isPending}
                  onClick={() => choose(code)}>
                  {code.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {/* The note is always there to add to, and demanded only where the code asks for one. */}
      <div className="arc-row" style={{ gap: 8 }}>
        <input className="arc-input" placeholder={needNote ? 'This one needs a note' : 'Note (optional)'}
          aria-label="Note" aria-invalid={needNote || undefined} value={note}
          onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setNeedNote(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && chosen && note.trim()) save.mutate({ code: chosen, note: note.trim() });
          }} />
        {chosen && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={save.isPending || !note.trim()}
            onClick={() => save.mutate({ code: chosen, note: note.trim() || null })}>
            Save note
          </button>
        )}
      </div>
      {needNote && (
        <div className="arc-error" style={{ marginTop: 0 }}>
          Add a note, then choose it again.
        </div>
      )}
    </section>
  );
}
